import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';

// Types
import { ReviewTemplateType, ReviewTemplateVersion } from 'Shared/types/ReviewTemplates.type';
import { ReviewFormTemplateType } from 'Shared/types/ReviewFormTemplate.type';

// Utils
import { getReviewTemplateErrors } from 'Shared/utils/ReviewTemplate.utils';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Selectors
import { getReviewTemplateBySlug } from 'Shared/selectors/ReviewTemplates.selectors';

// Actions
import {
  fetchReviewTemplate,
  updateReviewTemplate,
  duplicateReviewTemplate,
  deleteReviewTemplate,
} from 'Shared/actions/ReviewTemplates.actions';

type Props = {
  slug: string;
  withFetch?: boolean;
};

export const useReviewTemplate = ({ slug, withFetch = true }: Props) => {
  const dispatch = useDispatch();

  const { id: currentUserId } = useCurrentUser();
  const [isFetching, toggleIsFetching] = useToggle(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isDuplicating, toggleIsDuplicating] = useToggle(false);
  const [isDeleting, toggleIsDeleting] = useToggle(false);

  const template: ReviewTemplateType = useSelector(state => getReviewTemplateBySlug(state, { slug }));
  const reviewFormTemplates: ReviewFormTemplateType[] = template?.reviewFormTemplates || [];
  const reviewFormTemplateErrors = getReviewTemplateErrors({ reviewTemplate: template });
  const hasErrors = getReviewTemplateErrors({ reviewTemplate: template }).length > 0;

  const isVersionOne = template?.version === ReviewTemplateVersion.one;
  const isVersionTwo = template?.version === ReviewTemplateVersion.two;

  const isCurrentUserCreator = template?.isCustom && currentUserId === template.userId;

  const handleUpdateTemplate = ({ reviewTemplate }: { reviewTemplate: ReviewTemplateType }) => {
    setIsUpdating(true);
    return dispatch(
      updateReviewTemplate({
        slug,
        reviewTemplate,
      })
    ).then(() => setIsUpdating(false));
  };

  const debouncedUpdate = useRef(
    debounce(reviewTemplate => {
      //   dispatch(
      return handleUpdateTemplate({ reviewTemplate });
      // updateReviewTemplate({
      //   slug,
      //   reviewTemplate,
      // })
      //   ).then(() => setIsUpdating(false));
    }, 250)
  ).current;

  const handleUpdateTemplateWithDebounce = ({ reviewTemplate }: { reviewTemplate: ReviewTemplateType }) => {
    setIsUpdating(true);
    return debouncedUpdate({ reviewTemplate });
  };

  const handleUpdateReviewFormTemplate = ({
    reviewFormTemplate,
    withDebounce = false,
  }: {
    reviewFormTemplate: ReviewFormTemplateType;
    withDebounce?: boolean;
  }) => {
    const newReviewFormTemplates = reviewFormTemplates.map(rft =>
      rft.formType === reviewFormTemplate.formType ? reviewFormTemplate : rft
    );
    const newReviewTemplate = { reviewTemplate: { ...template, reviewFormTemplates: newReviewFormTemplates } };
    if (withDebounce) return handleUpdateTemplateWithDebounce(newReviewTemplate);
    else return handleUpdateTemplate(newReviewTemplate);
  };

  const handleDuplicateReviewTemplate = () => {
    toggleIsDuplicating();
    return dispatch(duplicateReviewTemplate({ name: `${template.name} - copy`, duplicateFromSlug: slug }));
  };

  const handleDeleteReviewTemplate = () => {
    toggleIsDeleting();
    return dispatch(deleteReviewTemplate({ slug }));
  };

  useEffect(() => {
    if (withFetch) {
      toggleIsFetching();
      dispatch(fetchReviewTemplate({ slug })).then(() => {
        toggleIsFetching();
      });
    }
  }, [withFetch]);

  return {
    isCurrentUserCreator,
    isFetching,
    template,
    reviewFormTemplates,
    handleUpdateTemplateWithDebounce,
    handleUpdateReviewFormTemplate,
    isUpdating,
    isDuplicating,
    handleDuplicateReviewTemplate,
    isDeleting,
    handleDeleteReviewTemplate,
    reviewFormTemplateErrors,
    hasErrors,
    isVersionOne,
    isVersionTwo,
  };
};
